import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import {
  Activity,
  ArrowRight,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Percent,
  RotateCcw,
  Settings,
  DollarSign
} from 'lucide-react';

const RecentTrustActivityWidget = () => {
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentActivity();
  }, []);

  const fetchRecentActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getRecentTrustActivity(5);
      setTransactions(response.data.transactions || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching recent trust activity:', error);
      setError('Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(absValue);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowUpRight className="h-4 w-4 text-green-600" />;
      case 'withdrawal':
        return <ArrowDownRight className="h-4 w-4 text-red-600" />;
      case 'transfer':
        return <RefreshCw className="h-4 w-4 text-blue-600" />;
      case 'interest':
        return <Percent className="h-4 w-4 text-purple-600" />;
      case 'refund':
        return <RotateCcw className="h-4 w-4 text-orange-600" />;
      case 'adjustment':
        return <Settings className="h-4 w-4 text-gray-600" />;
      case 'fee_earned':
        return <DollarSign className="h-4 w-4 text-yellow-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTransactionStyles = (type, amount) => {
    const isPositive = parseFloat(amount) > 0;
    if (type === 'deposit' || type === 'interest') {
      return { text: 'text-green-600', prefix: '+' };
    } else if (type === 'withdrawal' || type === 'fee_earned') {
      return { text: 'text-red-600', prefix: '-' };
    } else if (type === 'transfer') {
      return { text: 'text-blue-600', prefix: '' };
    } else if (type === 'refund') {
      return { text: 'text-orange-600', prefix: '-' };
    }
    return {
      text: isPositive ? 'text-green-600' : 'text-red-600',
      prefix: isPositive ? '+' : '-'
    };
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 w-36 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-14 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="mr-2 h-5 w-5 text-yellow-600" />
            Recent Trust Activity
          </h3>
          <button
            onClick={fetchRecentActivity}
            className="text-yellow-600 hover:text-gray-800 text-sm"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-4">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="mr-2 h-5 w-5 text-yellow-600" />
            Recent Activity
          </h3>
          <Link
            to="/trust-accounts"
            className="text-yellow-600 hover:text-gray-800 flex items-center text-sm font-medium"
          >
            View All
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Transaction List */}
      <div className="divide-y divide-gray-100">
        {transactions.length === 0 ? (
          <div className="p-6 text-center">
            <Activity className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No recent transactions</p>
          </div>
        ) : (
          transactions.map((transaction) => {
            const styles = getTransactionStyles(transaction.transaction_type, transaction.amount);
            return (
              <Link
                key={transaction.id}
                to={`/trust-accounts/${transaction.trust_account_id}`}
                className="block p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                      {getTransactionIcon(transaction.transaction_type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {transaction.client_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {transaction.description}
                      </p>
                      {transaction.case_number && (
                        <p className="text-xs text-yellow-600">
                          Case: {transaction.case_number}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className={`text-sm font-bold ${styles.text}`}>
                      {styles.prefix}{formatCurrency(transaction.amount)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(transaction.transaction_date)}
                    </p>
                    {transaction.status !== 'completed' && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStatusBadge(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Footer with total count */}
      {totalCount > 5 && (
        <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Showing 5 of {totalCount.toLocaleString()} transactions
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentTrustActivityWidget;
